// Radar- og antennedekning: ekte RF-fysikk over ekte høydedata.
//
// Alle formler er oppgitt i kommentar med kilde. Ingen tuning-konstanter uten
// begrunnelse, ingen «ser riktig ut»-tall.
//
// ── Formelsamling ────────────────────────────────────────────────────────────
// Radiohorisont (geometrisk horisont med refraksjon):
//     d = √(2·k·R·h)   →   d_km = 3.57·√(k·h_m)   (R = 6371 km ⇒ √(2R)/1000 = 3.5696)
//     k = 4/3 gir den kjente d_km = 4.12·√(h_m).
//     Kombinert horisont sender+mottaker: d = d(h_tx) + d(h_rx).
//
// Bølgelengde:  λ = c / f,  c = 299 792 458 m/s.
//
// Fresnel-soner (n-te sone, avstander i meter fra hvert endepunkt):
//     F_n = √(n·λ·d1·d2 / (d1+d2))
//   Ekvivalent ingeniørform (km, GHz):  F1 = 17.31·√(d1·d2 / (f_GHz·d_km))
//   Praktisk klaringskrav for «fri» linje: 0.6·F1 (ITU-R P.530) — ved den
//   klaringen er diffraksjonstapet ≈ 0 dB (se `knifeEdgeLossDb` ved v = −0.6√2).
//
// Fritt-rom-tap (Friis):
//     FSPL_dB = 20·log10(4π·d/λ) = 32.4478 + 20·log10(d_km) + 20·log10(f_MHz)
//
// Knivkant-diffraksjon (ITU-R P.526, enkelt hinder):
//     v = h·√(2·(d1+d2) / (λ·d1·d2))        (h > 0 = hinderet over siktlinja)
//       = √2 · h / F1
//     J(v) = 6.9 + 20·log10( √((v−0.1)² + 1) + v − 0.1 )  dB, for v > −0.7
//     J(v) = 0 dB for v ≤ −0.7
//   J(0) ≈ 6.0 dB (halv blokkering), J(1) ≈ 13.9 dB, J(2.4) ≈ 20.5 dB.
//
// BEGRENSNINGER (ærlig oppgitt):
//   * Kun ENKELT knivkant (Deygout hovedhinder). Flere kammer i serie
//     (Epstein–Peterson/Deygout-full) er IKKE implementert — tapet
//     UNDERVURDERES i terreng med flere åsrygger etter hverandre.
//   * Ingen refleksjon/flerveis-fading, ingen atmosfærisk absorpsjon (relevant
//     over ~10 GHz), ingen regndemping, ingen vegetasjonstap. Terrarium er DTM,
//     så skog og bygninger finnes ikke i dataene i det hele tatt.
//   * Radarligningen (RCS/støy) er ikke modellert — modulen svarer på om målet
//     er i SIKT, ikke om ekkoet er sterkt nok.
import { elevationAt, project } from '@skade/contracts';
import { DEFAULT_K_FACTOR, EARTH_RADIUS_M, earthDropM, effectiveEarthRadiusM, lineOfSightDetailed, normalizeAzimuthDeg, sectorSpanDeg, } from './los.js';
/** Lysfarten i vakuum (m/s). */
export const SPEED_OF_LIGHT_M_S = 299_792_458;
/** FSPL-konstanten for km + MHz: 20·log10(4π·1000·1e6/c). */
export const FSPL_CONST_KM_MHZ = 32.4477832;
/** ITU-R P.530 praktisk klaringskrav som andel av første Fresnel-sone. */
export const FRESNEL_CLEARANCE_FRACTION = 0.6;
const DEG = 180 / Math.PI;
// ── Grunnformler ─────────────────────────────────────────────────────────────
/** Bølgelengde (m) for frekvens i MHz. */
export function wavelengthM(freqMHz) {
    if (!(freqMHz > 0))
        throw new Error('wavelengthM: frekvens må være > 0 MHz');
    return SPEED_OF_LIGHT_M_S / (freqMHz * 1e6);
}
/** Radiohorisont i meter: d = √(2·k·R·h). */
export function radioHorizonM(antennaHeightM, kFactor = DEFAULT_K_FACTOR, earthRadiusM = EARTH_RADIUS_M) {
    if (antennaHeightM <= 0)
        return 0;
    return Math.sqrt(2 * effectiveEarthRadiusM(kFactor, earthRadiusM) * antennaHeightM);
}
/** Radiohorisont i km — den kjente d_km = 4.12·√(h_m) ved k = 4/3. */
export function radioHorizonKm(antennaHeightM, kFactor = DEFAULT_K_FACTOR, earthRadiusM = EARTH_RADIUS_M) {
    return radioHorizonM(antennaHeightM, kFactor, earthRadiusM) / 1000;
}
/** Kombinert radiohorisont (m) for to antenner — maks avstand for geometrisk sikt. */
export function combinedRadioHorizonM(heightAM, heightBM, kFactor = DEFAULT_K_FACTOR, earthRadiusM = EARTH_RADIUS_M) {
    return (radioHorizonM(heightAM, kFactor, earthRadiusM) + radioHorizonM(heightBM, kFactor, earthRadiusM));
}
/** Radius (m) i n-te Fresnel-sone: F_n = √(n·λ·d1·d2/(d1+d2)). */
export function fresnelZoneRadiusM(d1M, d2M, freqMHz, zone = 1) {
    const d = d1M + d2M;
    if (d <= 0 || d1M < 0 || d2M < 0)
        return 0;
    return Math.sqrt((zone * wavelengthM(freqMHz) * d1M * d2M) / d);
}
/** Klaringskrav (m) = fraksjon · F1. Standard 0.6·F1 (ITU-R P.530). */
export function fresnelClearanceRequiredM(d1M, d2M, freqMHz, fraction = FRESNEL_CLEARANCE_FRACTION) {
    return fraction * fresnelZoneRadiusM(d1M, d2M, freqMHz, 1);
}
/** Fritt-rom-tap (dB): 32.4478 + 20·log10(d_km) + 20·log10(f_MHz). */
export function fsplDb(distanceM, freqMHz) {
    if (!(distanceM > 0))
        throw new Error('fsplDb: avstand må være > 0 m');
    return FSPL_CONST_KM_MHZ + 20 * Math.log10(distanceM / 1000) + 20 * Math.log10(freqMHz);
}
/**
 * Diffraksjonsparameter v (ITU-R P.526). `obstructionAboveLosM` er hinderets
 * høyde OVER siktlinja: positiv = hinderet stikker over (blokkerer), negativ =
 * klaring. Merk identiteten v = √2·h/F1 — den er testet.
 */
export function diffractionParameterV(obstructionAboveLosM, d1M, d2M, freqMHz) {
    const d = d1M + d2M;
    if (d <= 0 || d1M <= 0 || d2M <= 0)
        return 0;
    return obstructionAboveLosM * Math.sqrt((2 * d) / (wavelengthM(freqMHz) * d1M * d2M));
}
/**
 * Knivkant-diffraksjonstap J(v) i dB (ITU-R P.526 tilnærming, gyldig v > −0.7).
 * Monotont voksende i v: mer inntrengning ⇒ aldri mindre tap.
 */
export function knifeEdgeLossDb(v) {
    if (v <= -0.7)
        return 0;
    return 6.9 + 20 * Math.log10(Math.sqrt((v - 0.1) ** 2 + 1) + v - 0.1);
}
/**
 * Full dekningsvurdering for en antenne/radar mot ett punkt: siktlinje med
 * jordkrumming + Fresnel-klaring + knivkant-diffraksjon + tap-budsjett.
 *
 * Returnerer null når endepunktenes bakkehøyde ikke finnes i DEM-et (ærlig
 * «vet ikke», ikke et falskt «blokkert»).
 */
export function assessLink(g, from, to, opts) {
    const los = lineOfSightDetailed(g, from, to, opts);
    if (!los)
        return null;
    const freqMHz = opts.freqMHz;
    const fraction = opts.fresnelFraction ?? FRESNEL_CLEARANCE_FRACTION;
    const blockedLossDb = opts.blockedLossDb ?? 20;
    const cableLossDb = opts.cableLossDb ?? 0;
    const D = los.distanceM;
    let worstRatio = Number.POSITIVE_INFINITY;
    let fresnelAtWorst = 0;
    let maxV = Number.NEGATIVE_INFINITY;
    let worstObstruction = null;
    let worstPenetration = 0;
    for (let i = 1; i < los.samples.length - 1; i++) {
        const s = los.samples[i];
        if (s.clearanceM === null || s.effectiveElevM === null || s.terrainElevM === null)
            continue;
        const d1 = s.distanceM;
        const d2 = D - d1;
        if (d1 <= 0 || d2 <= 0)
            continue;
        const f1 = fresnelZoneRadiusM(d1, d2, freqMHz, 1);
        if (f1 > 0) {
            const ratio = s.clearanceM / f1;
            if (ratio < worstRatio) {
                worstRatio = ratio;
                fresnelAtWorst = f1;
            }
        }
        // h = hinderets høyde over siktlinja = −klaring.
        const v = diffractionParameterV(-s.clearanceM, d1, d2, freqMHz);
        if (v > maxV)
            maxV = v;
        const penetration = -s.clearanceM;
        if (penetration > worstPenetration) {
            worstPenetration = penetration;
            worstObstruction = {
                distanceM: d1,
                lat: s.lat,
                lon: s.lon,
                terrainElevM: s.terrainElevM,
                effectiveElevM: s.effectiveElevM,
                sightElevM: s.sightElevM,
                penetrationM: penetration,
            };
        }
    }
    if (!Number.isFinite(worstRatio))
        worstRatio = Number.POSITIVE_INFINITY;
    const diffractionV = Number.isFinite(maxV) ? maxV : Number.NEGATIVE_INFINITY;
    const diffractionLossDb = Number.isFinite(diffractionV) ? knifeEdgeLossDb(diffractionV) : 0;
    const loss = fsplDb(D, freqMHz);
    const pathLossDb = loss + diffractionLossDb + cableLossDb;
    let receivedPowerDbm = null;
    let marginDb = null;
    if (opts.txPowerDbm !== undefined) {
        receivedPowerDbm =
            opts.txPowerDbm + (opts.txGainDbi ?? 0) + (opts.rxGainDbi ?? 0) - pathLossDb;
        if (opts.rxSensitivityDbm !== undefined)
            marginDb = receivedPowerDbm - opts.rxSensitivityDbm;
    }
    const horizonM = combinedRadioHorizonM(opts.observerHeightM ?? 1.7, opts.targetHeightM ?? 0, opts.kFactor ?? DEFAULT_K_FACTOR, opts.earthRadiusM ?? EARTH_RADIUS_M);
    let status;
    if (marginDb !== null && marginDb < 0)
        status = 'blokkert';
    else if (worstRatio >= fraction)
        status = 'fri';
    else if (worstRatio >= 0)
        status = 'fresnel-inntrengning';
    else if (diffractionLossDb > blockedLossDb)
        status = 'blokkert';
    else
        status = 'diffraksjon';
    return {
        status,
        distanceM: D,
        freqMHz,
        los,
        fsplDb: loss,
        diffractionLossDb,
        pathLossDb,
        worstFresnelRatio: worstRatio,
        fresnelRadiusAtWorstM: fresnelAtWorst,
        diffractionV,
        worstObstruction,
        receivedPowerDbm,
        marginDb,
        radioHorizonM: horizonM,
        beyondRadioHorizon: D > horizonM,
    };
}
/**
 * Dekningsflate: for hver asimut den avstanden dekningen slutter — terrenget
 * maskerer, radiohorisonten passeres, tap-budsjettet brytes, eller den
 * instrumenterte rekkevidden nås.
 *
 * Marsjen bruker samme radielle sveip-logikk som `sectorViewshed` (høyeste
 * terreng-elevasjonsvinkel så langt), men stopper ved FØRSTE punkt der målet
 * ikke lenger er synlig — det er «maks rekkevidde», ikke «synlige flekker».
 * Flekker bak et kam får man av `sectorViewshed`.
 */
export function coverageFootprint(g, site, opts) {
    const antennaHeightM = opts.antennaHeightM ?? 10;
    const targetHeightM = opts.targetHeightM ?? 0;
    const kFactor = opts.kFactor ?? DEFAULT_K_FACTOR;
    const earthRadiusM = opts.earthRadiusM ?? EARTH_RADIUS_M;
    const Re = effectiveEarthRadiusM(kFactor, earthRadiusM);
    const maxRangeM = opts.maxRangeM;
    if (!(maxRangeM > 0))
        throw new Error('coverageFootprint: maxRangeM må være > 0');
    const minRangeM = Math.max(0, opts.minRangeM ?? 0);
    const rangeStepM = Math.max(1, opts.rangeStepM ?? g.cellSizeM / 2);
    const siteGroundElevM = elevationAt(g, site.lat, site.lon);
    if (siteGroundElevM === null)
        return null; // ingen høydedata på stedet — vet ikke
    const siteAmslM = siteGroundElevM + antennaHeightM;
    // Klemmingen gjelder BARE det utledede steget; oppgitt steg respekteres.
    const azimuthStepDeg = opts.azimuthStepDeg !== undefined
        ? opts.azimuthStepDeg
        : Math.max(0.25, Math.min(2, ((g.cellSizeM * 0.5) / maxRangeM) * DEG));
    if (!(azimuthStepDeg > 0))
        throw new Error('coverageFootprint: azimuthStepDeg må være > 0');
    const from = normalizeAzimuthDeg(opts.fromAzimuthDeg ?? 0);
    const span = sectorSpanDeg(opts.fromAzimuthDeg ?? 0, opts.toAzimuthDeg ?? 360);
    const rayCount = Math.max(1, Math.ceil(span / azimuthStepDeg));
    // Radiohorisonten for det målet vi leter etter (bakkemål vs. fast flyhøyde).
    const targetAboveGroundForHorizon = opts.targetAltitudeAmslM !== undefined
        ? Math.max(0, opts.targetAltitudeAmslM - siteGroundElevM)
        : targetHeightM;
    const horizonM = combinedRadioHorizonM(antennaHeightM, targetAboveGroundForHorizon, kFactor, earthRadiusM);
    const rays = [];
    for (let a = 0; a < rayCount; a++) {
        const azimuthDeg = normalizeAzimuthDeg(from + (span * a) / rayCount);
        let maskTan = Number.NEGATIVE_INFINITY;
        let reach = minRangeM;
        let reachPoint = project(site, azimuthDeg, minRangeM);
        let limit = 'maks-rekkevidde';
        for (let d = rangeStepM; d <= maxRangeM + 1e-9; d += rangeStepM) {
            if (opts.maxPathLossDb !== undefined && fsplDb(d, opts.freqMHz) > opts.maxPathLossDb) {
                limit = 'tap-budsjett';
                break;
            }
            if (d > horizonM) {
                limit = 'radiohorisont';
                break;
            }
            const p = project(site, azimuthDeg, d);
            const terrainElevM = elevationAt(g, p.lat, p.lon);
            if (terrainElevM === null) {
                limit = 'ukjent-terreng';
                break;
            }
            const drop = (d * d) / (2 * Re);
            const targetAmsl = opts.targetAltitudeAmslM !== undefined
                ? opts.targetAltitudeAmslM
                : terrainElevM + targetHeightM;
            const targetTan = (targetAmsl - drop - siteAmslM) / d;
            if (d >= minRangeM && targetTan < maskTan) {
                limit = 'terreng';
                break;
            }
            const terrainTan = (terrainElevM - drop - siteAmslM) / d;
            if (terrainTan > maskTan)
                maskTan = terrainTan;
            reach = d;
            reachPoint = p;
        }
        rays.push({
            azimuthDeg,
            rangeM: reach,
            lat: reachPoint.lat,
            lon: reachPoint.lon,
            limit,
            maskingAngleDeg: Number.isFinite(maskTan) ? Math.atan(maskTan) * DEG : -90,
        });
    }
    const ring = rays.map((r) => [r.lon, r.lat]);
    if (ring.length > 0)
        ring.push(ring[0]); // lukk polygonet
    return {
        site,
        siteGroundElevM,
        siteAmslM,
        freqMHz: opts.freqMHz,
        rays,
        ring,
        minRangeM,
        maxRangeM,
    };
}
/**
 * Lavhøyde-maskering rundt en radar: per asimut den LAVESTE høyden et mål kan ha
 * ved `rangeM` og fortsatt bli sett. Under denne høyden flyr målet i radarens
 * terrengskygge.
 *
 *   h_min(AMSL) = h_antenne(AMSL) + d·tan(maskeringsvinkel) + d²/(2·k·R)
 *
 * Siste ledd er jordfallet: selv over blank sjø må målet stige for å komme over
 * horisonten. Returnerer null hvis radarpunktet mangler høydedata.
 */
export function radarLowAltitudeMask(g, site, opts) {
    const antennaHeightM = opts.antennaHeightM ?? 10;
    const kFactor = opts.kFactor ?? DEFAULT_K_FACTOR;
    const earthRadiusM = opts.earthRadiusM ?? EARTH_RADIUS_M;
    const Re = effectiveEarthRadiusM(kFactor, earthRadiusM);
    const D = opts.rangeM;
    if (!(D > 0))
        throw new Error('radarLowAltitudeMask: rangeM må være > 0');
    const siteGround = elevationAt(g, site.lat, site.lon);
    if (siteGround === null)
        return null;
    const siteAmslM = siteGround + antennaHeightM;
    const rangeStepM = Math.max(1, opts.rangeStepM ?? g.cellSizeM / 2);
    // Klemmingen gjelder BARE det utledede steget; oppgitt steg respekteres.
    const azimuthStepDeg = opts.azimuthStepDeg !== undefined
        ? opts.azimuthStepDeg
        : Math.max(0.25, Math.min(2, ((g.cellSizeM * 0.5) / D) * DEG));
    if (!(azimuthStepDeg > 0))
        throw new Error('radarLowAltitudeMask: azimuthStepDeg må være > 0');
    const from = normalizeAzimuthDeg(opts.fromAzimuthDeg ?? 0);
    const span = sectorSpanDeg(opts.fromAzimuthDeg ?? 0, opts.toAzimuthDeg ?? 360);
    const rayCount = Math.max(1, Math.ceil(span / azimuthStepDeg));
    const out = [];
    for (let a = 0; a < rayCount; a++) {
        const azimuthDeg = normalizeAzimuthDeg(from + (span * a) / rayCount);
        let maskTan = Number.NEGATIVE_INFINITY;
        let maskedAtRangeM = 0;
        for (let d = rangeStepM; d < D; d += rangeStepM) {
            const p = project(site, azimuthDeg, d);
            const elev = elevationAt(g, p.lat, p.lon);
            if (elev === null)
                continue;
            const tan = (elev - (d * d) / (2 * Re) - siteAmslM) / d;
            if (tan > maskTan) {
                maskTan = tan;
                maskedAtRangeM = d;
            }
        }
        const end = project(site, azimuthDeg, D);
        const groundElevM = elevationAt(g, end.lat, end.lon) ?? 0;
        const tanUsed = Number.isFinite(maskTan) ? maskTan : -siteAmslM / D;
        const minAmsl = siteAmslM + D * tanUsed + earthDropM(D, kFactor, earthRadiusM);
        out.push({
            azimuthDeg,
            rangeM: D,
            minAltitudeAmslM: Math.max(groundElevM, minAmsl),
            minAltitudeAglM: Math.max(0, minAmsl - groundElevM),
            groundElevM,
            maskingAngleDeg: Number.isFinite(maskTan) ? Math.atan(maskTan) * DEG : -90,
            maskedAtRangeM,
        });
    }
    return out;
}
/**
 * Maks avstand en radar/antenne kan se et mål i gitt HØYDE OVER HAVET, kun
 * geometrisk (kombinert radiohorisont). Rask sanity-grense uten DEM — bruk
 * `coverageFootprint` med `targetAltitudeAmslM` for terrengets faktiske svar.
 */
export function radarHorizonForTargetM(antennaHeightAglM, targetAltitudeAglM, kFactor = DEFAULT_K_FACTOR, earthRadiusM = EARTH_RADIUS_M) {
    return combinedRadioHorizonM(antennaHeightAglM, targetAltitudeAglM, kFactor, earthRadiusM);
}
//# sourceMappingURL=rf.js.map